import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Send, CheckCircle2, Circle, ArrowLeft, AlertTriangle, ShieldCheck } from 'lucide-react';
import { apiService } from '@/lib/api';
import { money, DRAW_STATUS, fmtDate, LENDER_BLUE, LENDER_NAVY, getLenderNotifyEmail, setLenderNotifyEmail } from '@/lib/lenderDemo';

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

export default function LenderBorrowerView() {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [amounts, setAmounts] = useState({});   // { line_item_id: "1234" }
  const [note, setNote] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(getLenderNotifyEmail());
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [resubmitId, setResubmitId] = useState(null);

  const load = useCallback(() => {
    apiService.lenderBorrowerView(loanId).then(({ data }) => {
      setData(data);
      const init = {};
      (data.doc_checklist || []).forEach((d) => { init[d.key] = false; });
      setDocs(init);
    }).catch(() => setData(false));
  }, [loanId]);
  useEffect(() => { load(); }, [load]);

  const bid = data && data.bid_sheet ? data.bid_sheet : { items: [], contingency_budget: 0, contingency_remaining: 0 };

  const calc = useMemo(() => {
    const lines = (bid.items || []).map((it) => {
      const value = num(amounts[it.line_item_id]);
      const over = Math.max(0, value - it.remaining);
      return { ...it, value, over };
    });
    const drawTotal = lines.reduce((s, l) => s + l.value, 0);
    const totalOver = lines.reduce((s, l) => s + l.over, 0);
    const contRemaining = bid.contingency_remaining || 0;
    const contUsed = Math.min(totalOver, contRemaining);
    const uncovered = Math.max(0, totalOver - contRemaining);
    return { lines, drawTotal, totalOver, contUsed, uncovered };
  }, [bid, amounts]);

  if (data === false) return <div className="p-10 text-center text-slate-400">Loan not found.</div>;
  if (!data) return <div className="p-10 text-center text-slate-400">Loading…</div>;

  const { loan, draws, doc_checklist } = data;
  const documents = (doc_checklist || []).map((d) => ({ ...d, provided: !!docs[d.key] }));
  const funds = data.funds || { remaining: 0, exhausted: false, approved_total: 0 };

  const requestMore = async () => {
    const input = window.prompt('How much additional funding do you need? (approved funds are exhausted)');
    if (input === null) return;
    const amt = parseFloat(input) || 0;
    await apiService.lenderRequestAdditionalFunds(loanId, { amount: amt, note: 'Approved funds exhausted' });
    setToast('Request for additional funds sent to your lender.');
  };

  const submit = async () => {
    if (calc.drawTotal <= 0) { setToast('Enter a draw amount on at least one line.'); return; }
    setBusy(true);
    const line_items = calc.lines.filter((l) => l.value > 0).map((l) => ({
      line_item_id: l.line_item_id, line: l.line, category: l.category,
      approved_budget: l.approved_budget, drawn_to_date: l.drawn_to_date, this_draw: l.value,
    }));
    const payload = { amount: calc.drawTotal, line_items, documents, note, notify_email: notifyEmail };
    try {
      if (resubmitId) await apiService.lenderResubmitDraw(loanId, resubmitId, payload);
      else await apiService.lenderSubmitDraw(loanId, payload);
      setToast(resubmitId ? 'Draw resubmitted to your lender.' : `Draw sent to ${loan.loan_officer_name} at your lender.`);
      setAmounts({}); setNote(''); setResubmitId(null);
      const init = {}; (doc_checklist || []).forEach((d) => { init[d.key] = false; }); setDocs(init);
      load();
    } finally { setBusy(false); }
  };

  const startResubmit = (d) => {
    setResubmitId(d.id);
    const amts = {};
    (d.line_items || []).forEach((li) => { if (li.line_item_id) amts[li.line_item_id] = String(li.this_draw ?? li.amount ?? ''); });
    setAmounts(amts);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F5F9' }}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm" style={{ background: LENDER_BLUE }}>D</div>
            <span className="font-bold" style={{ color: LENDER_NAVY }}>DrawReady</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Borrower</span>
          </div>
          <button onClick={() => navigate('/lender/demo')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Lender demo
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6">
        <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>{loan.borrower_name}</h1>
        <p className="text-slate-500 text-sm mb-4 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> {loan.project_name} · Lender officer: {loan.loan_officer_name}</p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[['Approved budget', loan.budget], ['Drawn to date', loan.funded_to_date], ['Remaining', loan.remaining]].map(([l, v]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500">{l}</div>
              <div className="text-lg font-bold mt-1" style={{ color: LENDER_NAVY }}>{money(v)}</div>
            </div>
          ))}
        </div>

        {funds.exhausted && (
          <div className="mb-5 rounded-xl px-4 py-3 bg-amber-50 border border-amber-200 flex items-center justify-between gap-3" data-testid="borrower-funds-exhausted">
            <div className="text-sm text-amber-800">Your approved funds are fully drawn. Need more to finish? Submit a request to your lender.</div>
            <button onClick={requestMore} data-testid="borrower-request-more"
              className="flex-shrink-0 text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}>
              Request additional funds
            </button>
          </div>
        )}

        {/* Request a draw — from the approved bid sheet */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold" style={{ color: LENDER_NAVY }}>{resubmitId ? 'Resubmit draw' : 'Request a draw'}</h3>
              <p className="text-xs text-slate-500">Draw against your approved bid sheet (schedule of values). Enter this draw amount per line.</p>
            </div>
            {resubmitId && <button onClick={() => { setResubmitId(null); setAmounts({}); }} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left font-semibold px-5 py-2.5">Line item</th>
                  <th className="text-right font-semibold px-3 py-2.5">Approved</th>
                  <th className="text-right font-semibold px-3 py-2.5 hidden sm:table-cell">Drawn to date</th>
                  <th className="text-right font-semibold px-3 py-2.5">Remaining</th>
                  <th className="text-right font-semibold px-5 py-2.5 w-40">This draw</th>
                </tr>
              </thead>
              <tbody>
                {calc.lines.map((l) => (
                  <tr key={l.line_item_id} className="border-b border-slate-50 last:border-0" data-testid={`bidline-${l.line_item_id}`}>
                    <td className="px-5 py-2">
                      <div className="font-medium" style={{ color: LENDER_NAVY }}>{l.line}</div>
                      <div className="text-[11px] text-slate-400">{l.category}</div>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">{money(l.approved_budget)}</td>
                    <td className="px-3 py-2 text-right text-slate-500 hidden sm:table-cell">{money(l.drawn_to_date)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{money(l.remaining)}</td>
                    <td className="px-5 py-2">
                      <input type="number" min="0" value={amounts[l.line_item_id] || ''}
                        onChange={(e) => setAmounts((s) => ({ ...s, [l.line_item_id]: e.target.value }))}
                        data-testid={`bidline-input-${l.line_item_id}`} placeholder="0"
                        className={`w-full text-right px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 ${l.over > 0 ? 'border-amber-300 focus:ring-amber-200 bg-amber-50' : 'border-slate-200 focus:ring-blue-200'}`} />
                      {l.over > 0 && <div className="text-[10px] text-amber-600 text-right mt-0.5">{money(l.over)} over budget</div>}
                    </td>
                  </tr>
                ))}
                {calc.lines.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No bid sheet found for this loan.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                  <td className="px-5 py-3" style={{ color: LENDER_NAVY }}>Total this draw</td>
                  <td></td><td className="hidden sm:table-cell"></td><td></td>
                  <td className="px-5 py-3 text-right text-lg" data-testid="borrower-draw-total" style={{ color: LENDER_BLUE }}>{money(calc.drawTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Over-budget / contingency callout */}
          {calc.totalOver > 0 && (
            <div className="mx-5 mb-4 rounded-lg px-3 py-2.5 text-sm flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800" data-testid="borrower-over-budget">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                This draw is <strong>{money(calc.totalOver)}</strong> over the approved budget.
                {calc.contUsed > 0 && <> <strong>{money(calc.contUsed)}</strong> will draw from contingency.</>}
                {calc.uncovered > 0 && <> <strong>{money(calc.uncovered)}</strong> exceeds budget + contingency — your lender will see this flagged.</>}
                {' '}You can still submit.
              </span>
            </div>
          )}

          <div className="px-5 pb-5 grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Attach compliance documents</label>
              <div className="space-y-1.5 mb-3">
                {(doc_checklist || []).map((d) => (
                  <button key={d.key} type="button" onClick={() => setDocs((s) => ({ ...s, [d.key]: !s[d.key] }))}
                    data-testid={`borrower-doc-${d.key}`} className="flex items-center gap-2 text-sm w-full text-left">
                    {docs[d.key] ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
                    <span className={docs[d.key] ? 'text-slate-700' : 'text-slate-500'}>{d.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400">Tip: leave one unchecked to show the lender flagging a missing item but still approving.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Note (optional)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                className="w-full mb-3 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <label className="block text-xs font-medium text-slate-500 mb-1">Email the lender notification to <span className="text-slate-400">(demo)</span></label>
              <input type="email" value={notifyEmail} data-testid="borrower-notify-email"
                onChange={(e) => { setNotifyEmail(e.target.value); setLenderNotifyEmail(e.target.value); }}
                placeholder="you@email.com"
                className="w-full mb-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <p className="text-[11px] text-slate-400 mb-3">Shows the lender's heads-up actually landing during your demo. Leave blank to skip the real email.</p>
              {toast && <p className="text-xs mb-2" style={{ color: LENDER_BLUE }} data-testid="borrower-toast">{toast}</p>}
              <button onClick={submit} disabled={busy} data-testid="borrower-submit-draw"
                className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-60" style={{ background: LENDER_BLUE }}>
                <Send className="w-4 h-4" /> {busy ? 'Sending…' : resubmitId ? 'Resubmit to lender' : 'Submit to lender'}
              </button>
            </div>
          </div>
        </div>

        {/* Draw history */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 font-semibold text-sm flex items-center gap-1.5" style={{ color: LENDER_NAVY }}>
            <ShieldCheck className="w-4 h-4" /> My draws
          </div>
          <table className="w-full text-sm">
            <tbody>
              {draws.map((d) => {
                const chip = DRAW_STATUS[d.status] || DRAW_STATUS.submitted;
                return (
                  <tr key={d.id} className="border-b border-slate-50 last:border-0" data-testid={`borrower-draw-${d.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium" style={{ color: LENDER_NAVY }}>Draw #{d.draw_number}</div>
                      <div className="text-xs text-slate-400">{fmtDate(d.submitted_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{money(d.amount)}</td>
                    <td className="px-4 py-3">
                      {d.over_budget && <span className="text-[10px] font-semibold text-amber-600 mr-2">over budget</span>}
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {d.status === 'sent_back' && (
                        <button onClick={() => startResubmit(d)} data-testid={`borrower-resubmit-${d.id}`}
                          className="text-xs font-semibold" style={{ color: LENDER_BLUE }}>Resubmit</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {draws.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">No draws yet — request your first one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
